import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Plus,
  CheckCircle,
  Clock,
  Camera,
  FileText,
  Users,
  Package,
  DollarSign,
  Calendar,
  MapPin,
  Phone,
  Mail,
  MoreVertical,
  ChevronDown,
  ChevronRight,
  Upload,
  Sparkles,
  Share2,
  Copy,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
  completed: { label: 'Completed', color: 'bg-green-500 text-white' },
  active: { label: 'Active', color: 'bg-blue-500 text-white' },
  pursuing: { label: 'Pursuing', color: 'bg-yellow-500 text-white' },
  bid_submitted: { label: 'Bid Submitted', color: 'bg-purple-500 text-white' },
  walkthrough_scheduled: { label: 'Walkthrough', color: 'bg-indigo-500 text-white' },
  on_hold: { label: 'On Hold', color: 'bg-gray-500 text-white' },
  lost: { label: 'Lost', color: 'bg-red-500 text-white' }
};

const tabs = [
  { id: 'overview', label: 'Overview', icon: FileText },
  { id: 'timeline', label: 'Timeline', icon: Calendar },
  { id: 'team', label: 'Team', icon: Users },
  { id: 'equipment', label: 'Equipment', icon: Package },
  { id: 'budget', label: 'Budget', icon: DollarSign },
  { id: 'photos', label: 'Photos', icon: Camera },
  { id: 'documents', label: 'Documents', icon: FileText },
];

export const JobDetail = () => {
  const { id } = useParams();
  const { api, isCOO } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [phases, setPhases] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showNewPhase, setShowNewPhase] = useState(false);
  const [newPhase, setNewPhase] = useState({ name: '', description: '' });
  const [generatingDoc, setGeneratingDoc] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [creatingShareLink, setCreatingShareLink] = useState(false);

  useEffect(() => {
    fetchJobData();
  }, [id]);

  const fetchJobData = async () => {
    try {
      const [jobRes, phasesRes, docsRes] = await Promise.all([
        api.get(`/jobs/${id}`),
        api.get(`/jobs/${id}/phases`),
        api.get(`/documents/${id}`)
      ]);
      setJob(jobRes.data);
      setPhases(phasesRes.data);
      setDocuments(docsRes.data);
    } catch (error) {
      console.error('Error fetching job:', error);
      toast.error('Failed to load job');
      navigate('/portal/jobs');
    } finally {
      setLoading(false);
    }
  };

  const updateJobStatus = async (newStatus) => {
    try {
      await api.patch(`/jobs/${id}`, { status: newStatus });
      setJob({ ...job, status: newStatus });
      setShowStatusMenu(false);
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const createPhase = async () => {
    try {
      const response = await api.post(`/jobs/${id}/phases`, {
        ...newPhase,
        order: phases.length,
        tasks: []
      });
      setPhases([...phases, response.data]);
      setNewPhase({ name: '', description: '' });
      setShowNewPhase(false);
      toast.success('Phase created');
    } catch (error) {
      toast.error('Failed to create phase');
    }
  };

  const toggleTask = async (phaseId, taskId, currentStatus) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    try {
      await api.patch(`/jobs/${id}/phases/${phaseId}/tasks/${taskId}`, { status: newStatus });
      fetchJobData();
      toast.success('Task updated');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const generateDocument = async (docType) => {
    setGeneratingDoc(true);
    try {
      const formData = new FormData();
      formData.append('job_id', id);
      formData.append('doc_type', docType);
      
      const response = await api.post('/documents/generate', formData);
      setDocuments([...documents, response.data]);
      toast.success(`${docType.toUpperCase()} generated successfully`);
    } catch (error) {
      console.error('Error generating document:', error);
      toast.error('Failed to generate document');
    } finally {
      setGeneratingDoc(false);
    }
  };

  const createShareLink = async () => {
    setCreatingShareLink(true);
    try {
      const response = await api.post('/share/create', {
        job_id: id,
        expires_in_days: 30
      });
      setShareLink(response.data);
      setShowShareModal(true);
      toast.success('Share link created');
    } catch (error) {
      console.error('Error creating share link:', error);
      toast.error('Failed to create share link');
    } finally {
      setCreatingShareLink(false);
    }
  };

  const copyShareLink = () => {
    if (shareLink?.share_url) {
      navigator.clipboard.writeText(shareLink.share_url);
      toast.success('Link copied to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="space-y-6" data-testid="job-detail">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <button
            onClick={() => navigate('/portal/jobs')}
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Jobs
          </button>
          <h1 className="font-heading text-3xl">{job.client_name}</h1>
          <p className="text-gray-500">{job.project_type} • {job.address}, {job.city}</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Share Button */}
          <button
            onClick={createShareLink}
            disabled={creatingShareLink}
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#C6A87C] text-[#C6A87C] text-xs font-semibold uppercase hover:bg-[#C6A87C]/5 disabled:opacity-50"
            data-testid="share-job-btn"
          >
            {creatingShareLink ? (
              <div className="w-4 h-4 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
            ) : (
              <Share2 className="w-4 h-4" />
            )}
            Share with Client
          </button>
          
          {/* Status Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowStatusMenu(!showStatusMenu)}
              className={`px-4 py-2 text-xs font-semibold uppercase flex items-center gap-2 ${statusConfig[job.status]?.color}`}
            >
              {statusConfig[job.status]?.label}
              <ChevronDown className="w-4 h-4" />
            </button>
            {showStatusMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowStatusMenu(false)} />
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-100 shadow-lg z-20">
                  {Object.entries(statusConfig).map(([key, { label }]) => (
                    <button
                      key={key}
                      onClick={() => updateJobStatus(key)}
                      className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                        job.status === key ? 'bg-gray-50 font-medium' : ''
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          
          <Link
            to={`/portal/jobs/${id}/edit`}
            className="p-2 border border-gray-200 hover:bg-gray-50"
          >
            <Edit className="w-5 h-5 text-gray-500" />
          </Link>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border border-gray-100 p-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium">Overall Progress</span>
          <span className="text-sm text-gray-500">{Math.round(job.completion_percentage)}%</span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${job.completion_percentage}%` }}
            transition={{ duration: 0.5 }}
            className="h-full bg-[#C6A87C]"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'border-[#C6A87C] text-[#C6A87C]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white border border-gray-100 p-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Client Info */}
            <div>
              <h3 className="font-heading text-lg mb-4">Client Information</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-gray-600">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>{job.address}, {job.city}</span>
                </div>
                {job.client_email && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <a href={`mailto:${job.client_email}`} className="hover:text-[#C6A87C]">{job.client_email}</a>
                  </div>
                )}
                {job.client_phone && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <a href={`tel:${job.client_phone}`} className="hover:text-[#C6A87C]">{job.client_phone}</a>
                  </div>
                )}
              </div>
            </div>

            {/* Project Details */}
            <div>
              <h3 className="font-heading text-lg mb-4">Project Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50">
                  <p className="text-xs text-gray-500 uppercase mb-1">Estimated Value</p>
                  <p className="font-heading text-xl">${job.estimated_value?.toLocaleString()}</p>
                </div>
                <div className="p-4 bg-gray-50">
                  <p className="text-xs text-gray-500 uppercase mb-1">Actual Cost</p>
                  <p className="font-heading text-xl">${job.actual_cost?.toLocaleString()}</p>
                </div>
                {job.start_date && (
                  <div className="p-4 bg-gray-50">
                    <p className="text-xs text-gray-500 uppercase mb-1">Start Date</p>
                    <p className="font-medium">{new Date(job.start_date).toLocaleDateString()}</p>
                  </div>
                )}
                {job.projected_end_date && (
                  <div className="p-4 bg-gray-50">
                    <p className="text-xs text-gray-500 uppercase mb-1">End Date</p>
                    <p className="font-medium">{new Date(job.projected_end_date).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            {job.description && (
              <div className="lg:col-span-2">
                <h3 className="font-heading text-lg mb-4">Description</h3>
                <p className="text-gray-600 whitespace-pre-wrap">{job.description}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="font-heading text-lg">Project Phases</h3>
              <button
                onClick={() => setShowNewPhase(true)}
                className="inline-flex items-center gap-2 bg-[#C6A87C] text-white px-4 py-2 text-xs font-semibold uppercase hover:bg-[#B09265]"
              >
                <Plus className="w-4 h-4" />
                Add Phase
              </button>
            </div>

            {/* New Phase Form */}
            {showNewPhase && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="bg-gray-50 p-4 border border-gray-200"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <input
                    type="text"
                    placeholder="Phase name (e.g., Demo, Framing, Paint)"
                    value={newPhase.name}
                    onChange={(e) => setNewPhase({ ...newPhase, name: e.target.value })}
                    className="px-4 py-2 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                  />
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    value={newPhase.description}
                    onChange={(e) => setNewPhase({ ...newPhase, description: e.target.value })}
                    className="px-4 py-2 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={createPhase}
                    disabled={!newPhase.name}
                    className="px-4 py-2 bg-[#C6A87C] text-white text-xs font-semibold uppercase disabled:opacity-50"
                  >
                    Create Phase
                  </button>
                  <button
                    onClick={() => setShowNewPhase(false)}
                    className="px-4 py-2 border border-gray-200 text-xs font-semibold uppercase"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            )}

            {/* Phases List */}
            {phases.length > 0 ? (
              <div className="space-y-4">
                {phases.map((phase, index) => (
                  <PhaseCard
                    key={phase.id}
                    phase={phase}
                    jobId={id}
                    api={api}
                    onUpdate={fetchJobData}
                    toggleTask={toggleTask}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No phases yet. Add your first phase to start tracking progress.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <h3 className="font-heading text-lg">Documents</h3>
              <div className="flex flex-wrap gap-2">
                {['sow', 'sov', 'bid', 'contract', 'mou'].map((docType) => (
                  <button
                    key={docType}
                    onClick={() => generateDocument(docType)}
                    disabled={generatingDoc}
                    className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 text-xs font-semibold uppercase hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Sparkles className="w-3 h-3" />
                    Generate {docType.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {generatingDoc && (
              <div className="flex items-center gap-3 p-4 bg-[#C6A87C]/10 border border-[#C6A87C]/20">
                <div className="w-5 h-5 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-[#C6A87C]">Generating document with AI...</span>
              </div>
            )}

            {documents.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {documents.map((doc) => (
                  <div key={doc.id} className="py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-gray-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-gray-500" />
                      </div>
                      <div>
                        <p className="font-medium">{doc.name}</p>
                        <p className="text-sm text-gray-500">
                          {doc.type.toUpperCase()} • {new Date(doc.created_at).toLocaleDateString()}
                          {doc.ai_generated && <span className="ml-2 text-[#C6A87C]">• AI Generated</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {doc.shared_with_client && (
                        <span className="px-2 py-1 text-xs bg-green-100 text-green-700">Shared</span>
                      )}
                      <button className="p-2 hover:bg-gray-100">
                        <MoreVertical className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No documents yet. Generate or upload documents for this job.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'team' && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">Team assignment coming soon</p>
            <Link to="/portal/team" className="text-[#C6A87C] text-sm mt-2 inline-block hover:underline">
              Go to Team Builder
            </Link>
          </div>
        )}

        {activeTab === 'equipment' && (
          <div className="text-center py-12">
            <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">Equipment checkout coming soon</p>
            <Link to="/portal/inventory" className="text-[#C6A87C] text-sm mt-2 inline-block hover:underline">
              Go to Inventory
            </Link>
          </div>
        )}

        {activeTab === 'budget' && (
          <div className="text-center py-12">
            <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">Budget tracking coming soon</p>
            {isCOO && (
              <Link to="/portal/accounting" className="text-[#C6A87C] text-sm mt-2 inline-block hover:underline">
                Go to Accounting
              </Link>
            )}
          </div>
        )}

        {activeTab === 'photos' && (
          <div className="text-center py-12">
            <Camera className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">Photo gallery coming soon</p>
            <p className="text-sm text-gray-400 mt-2">Upload progress photos from the Timeline tab</p>
          </div>
        )}
      </div>

      {/* Share Modal */}
      {showShareModal && shareLink && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowShareModal(false)}>
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white w-full max-w-md p-6"
            onClick={e => e.stopPropagation()}
          >
            <h2 className="font-heading text-xl mb-4">Share Project with Client</h2>
            <p className="text-gray-600 text-sm mb-4">
              Share this link with your client so they can view project progress, timeline, and shared documents.
            </p>
            
            <div className="bg-gray-50 p-3 flex items-center gap-2 mb-4">
              <input
                type="text"
                value={shareLink.share_url}
                readOnly
                className="flex-1 bg-transparent text-sm text-gray-700 outline-none truncate"
              />
              <button
                onClick={copyShareLink}
                className="p-2 hover:bg-gray-200 transition-colors"
                title="Copy link"
              >
                <Copy className="w-4 h-4 text-gray-500" />
              </button>
              <a
                href={shareLink.share_url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 hover:bg-gray-200 transition-colors"
                title="Open in new tab"
              >
                <ExternalLink className="w-4 h-4 text-gray-500" />
              </a>
            </div>

            <div className="text-xs text-gray-400 mb-6">
              <p>Link expires: {new Date(shareLink.expires_at).toLocaleDateString()}</p>
              <p>Only shared documents will be visible to the client.</p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowShareModal(false)}
                className="px-4 py-2 border border-gray-200 text-gray-600 text-xs font-semibold uppercase"
              >
                Close
              </button>
              <button
                onClick={copyShareLink}
                className="px-4 py-2 bg-[#C6A87C] text-white text-xs font-semibold uppercase hover:bg-[#B09265]"
              >
                Copy Link
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

// Phase Card Component
const PhaseCard = ({ phase, jobId, api, onUpdate, toggleTask }) => {
  const [expanded, setExpanded] = useState(true);
  const [newTask, setNewTask] = useState('');
  const [showAddTask, setShowAddTask] = useState(false);

  const addTask = async () => {
    if (!newTask.trim()) return;
    try {
      // Add task to phase
      const updatedTasks = [...(phase.tasks || []), {
        id: `task-${Date.now()}`,
        name: newTask,
        status: 'pending',
        description: null,
        due_date: null,
        assigned_to: null,
        photo_url: null
      }];
      
      await api.patch(`/jobs/${jobId}/phases/${phase.id}/tasks/${updatedTasks[updatedTasks.length - 1].id}`, {
        name: newTask,
        status: 'pending'
      });
      
      setNewTask('');
      setShowAddTask(false);
      onUpdate();
      toast.success('Task added');
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  const completedTasks = phase.tasks?.filter(t => t.status === 'completed').length || 0;
  const totalTasks = phase.tasks?.length || 0;

  return (
    <div className="border border-gray-200">
      {/* Phase Header */}
      <div
        className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
          <div>
            <h4 className="font-medium">{phase.name}</h4>
            {phase.description && <p className="text-sm text-gray-500">{phase.description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">{completedTasks}/{totalTasks} tasks</span>
          <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#C6A87C]"
              style={{ width: `${phase.completion_percentage}%` }}
            />
          </div>
          <span className="text-sm font-medium">{Math.round(phase.completion_percentage)}%</span>
        </div>
      </div>

      {/* Tasks */}
      {expanded && (
        <div className="p-4 space-y-2">
          {phase.tasks?.map((task) => (
            <div
              key={task.id}
              className={`flex items-center gap-3 p-3 ${
                task.status === 'completed' ? 'bg-green-50' : 'bg-gray-50'
              }`}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTask(phase.id, task.id, task.status);
                }}
                className={`w-6 h-6 border-2 flex items-center justify-center ${
                  task.status === 'completed'
                    ? 'bg-green-500 border-green-500 text-white'
                    : 'border-gray-300 hover:border-[#C6A87C]'
                }`}
              >
                {task.status === 'completed' && <CheckCircle className="w-4 h-4" />}
              </button>
              <span className={task.status === 'completed' ? 'line-through text-gray-400' : ''}>
                {task.name}
              </span>
              {task.photo_url && (
                <Camera className="w-4 h-4 text-green-500 ml-auto" />
              )}
            </div>
          ))}

          {/* Add Task */}
          {showAddTask ? (
            <div className="flex gap-2 mt-2">
              <input
                type="text"
                placeholder="Task name..."
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addTask()}
                className="flex-1 px-3 py-2 border border-gray-200 focus:border-[#C6A87C] focus:outline-none text-sm"
                autoFocus
              />
              <button
                onClick={addTask}
                className="px-4 py-2 bg-[#C6A87C] text-white text-xs font-semibold uppercase"
              >
                Add
              </button>
              <button
                onClick={() => setShowAddTask(false)}
                className="px-4 py-2 border border-gray-200 text-xs font-semibold uppercase"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddTask(true)}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#C6A87C] mt-2"
            >
              <Plus className="w-4 h-4" />
              Add Task
            </button>
          )}
        </div>
      )}
    </div>
  );
};
